// src/api/supportTickets/supportTicketsApi.ts

import { ENV } from '../../config/runtimeEnv';
import {
	SupportTicket,
	SupportTicketMessage,
	CreateSupportTicketRequest,
	AddMessageRequest,
	UpdateStatusRequest,
	AssignTicketRequest,
	SupportTicketStats,
	ApiResponse,
} from '../../types/communications/supportTickets';
import type { UserUuid } from '../../types/identity';
import { createServiceClient } from '../../lib/http/client';

export const API_BASE_URL = `${ENV.VITE_HEALTHCARE_SERVER}/admin`;

const createApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

const createMultipartApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
};

export const getSupportTickets = async (params: {
	user_id?: UserUuid;
	company_id?: number;
	status?: string;
	assigned_to?: UserUuid;
	priority?: string;
	is_admin?: boolean | string;
}): Promise<SupportTicket[]> => {

	params = {
		...params,
		is_admin: params.is_admin ? '1' : '0'
	}

	try {
		const api = createApiInstance();
		const response = await api.get<ApiResponse<SupportTicket[]>>('/support-tickets', { params });
		return response.data.data || [];
	} catch (error) {
		console.error('Error fetching support tickets:', error);
		throw error;
	}
};

export const getSupportTicket = async (ticketId: string, userId: UserUuid): Promise<SupportTicket> => {
	try {
		const api = createApiInstance();
		const response = await api.get<ApiResponse<SupportTicket>>(`/support-tickets/${ticketId}`, {
			params: { user_id: userId },
		});
		return response.data.data!;
	} catch (error) {
		console.error('Error fetching support ticket:', error);
		throw error;
	}
};

export const createSupportTicket = async (request: CreateSupportTicketRequest): Promise<SupportTicket> => {
	try {
		const hasFiles = request.attachments && request.attachments.length > 0;

		if (hasFiles) {
			const api = createMultipartApiInstance();
			const formData = new FormData();

			formData.append('user_id', request.user_id.toString());
			formData.append('company_id', request.company_id.toString());
			formData.append('subject', request.subject);
			formData.append('description', request.description);

			if (request.priority) {
				formData.append('priority', request.priority);
			}

			if (request.category) {
				formData.append('category', request.category);
			}

			request.attachments!.forEach((file) => {
				formData.append('attachments[]', file);
			});

			const response = await api.post<ApiResponse<SupportTicket>>('/support-tickets', formData);
			return response.data.data!;
		} else {
			const api = createApiInstance();
			const response = await api.post<ApiResponse<SupportTicket>>('/support-tickets', {
				user_id: request.user_id,
				company_id: request.company_id,
				subject: request.subject,
				description: request.description,
				priority: request.priority,
				category: request.category,
			});
			return response.data.data!;
		}
	} catch (error) {
		console.error('Error creating support ticket:', error);
		throw error;
	}
};

export const addMessage = async (ticketId: string, request: AddMessageRequest): Promise<SupportTicketMessage> => {
	try {
		const hasFiles = request.attachments && request.attachments.length > 0;

		if (hasFiles) {
			const api = createMultipartApiInstance();
			const formData = new FormData();

			formData.append('sender_user_id', request.sender_user_id.toString());
			formData.append('message', request.message);

			if (request.is_internal_note !== undefined) {
				formData.append('is_internal_note', request.is_internal_note ? '1' : '0');
			}

			request.attachments!.forEach((file) => {
				formData.append('attachments[]', file);
			});

			const response = await api.post<ApiResponse<SupportTicketMessage>>(
				`/support-tickets/${ticketId}/messages`,
				formData
			);
			return response.data.data!;
		} else {
			const api = createApiInstance();
			const response = await api.post<ApiResponse<SupportTicketMessage>>(
				`/support-tickets/${ticketId}/messages`,
				{
					sender_user_id: request.sender_user_id,
					message: request.message,
					is_internal_note: request.is_internal_note,
				}
			);
			return response.data.data!;
		}
	} catch (error) {
		console.error('Error adding message:', error);
		throw error;
	}
};

export const updateTicketStatus = async (ticketId: string, request: UpdateStatusRequest): Promise<SupportTicket> => {
	try {
		const api = createApiInstance();
		const response = await api.put<ApiResponse<SupportTicket>>(
			`/support-tickets/${ticketId}/status`,
			request
		);
		return response.data.data!;
	} catch (error) {
		console.error('Error updating ticket status:', error);
		throw error;
	}
};

export const assignTicket = async (ticketId: string, request: AssignTicketRequest): Promise<SupportTicket> => {
	try {
		const api = createApiInstance();
		const response = await api.post<ApiResponse<SupportTicket>>(
			`/support-tickets/${ticketId}/assign`,
			request
		);
		return response.data.data!;
	} catch (error) {
		console.error('Error assigning ticket:', error);
		throw error;
	}
};

export const unassignTicket = async (ticketId: string, userId: UserUuid): Promise<SupportTicket> => {
	try {
		const api = createApiInstance();
		const response = await api.post<ApiResponse<SupportTicket>>(
			`/support-tickets/${ticketId}/unassign`,
			{ user_id: userId }
		);
		return response.data.data!;
	} catch (error) {
		console.error('Error unassigning ticket:', error);
		throw error;
	}
};

export const getSupportTicketStats = async (companyId: number): Promise<SupportTicketStats> => {
	try {
		const api = createApiInstance();
		const response = await api.get<SupportTicketStats>('/support-tickets/stats', {
			params: { company_id: companyId },
		});
		return response.data;
	} catch (error) {
		console.error('Error fetching support ticket stats:', error);
		throw error;
	}
};

export const getStatusColor = (status: string): string => {
	const statusMap: { [key: string]: string } = {
		open: 'blue',
		in_progress: 'processing',
		waiting_user: 'warning',
		waiting_admin: 'orange',
		resolved: 'success',
		closed: 'default',
	};
	return statusMap[status] || 'default';
};

export const getPriorityColor = (priority: string): string => {
	const priorityMap: { [key: string]: string } = {
		low: 'default',
		normal: 'blue',
		high: 'orange',
		urgent: 'red',
	};
	return priorityMap[priority] || 'default';
};

export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};